import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronDown, ChevronUp, Maximize2, Search } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { ansiLines, stripAnsi } from '../lib/ansi.js';
import { termPieces } from '../lib/term.js';
import { CopyButton } from './CodeBlock.js';
import { cx, StatusPill } from './primitives.js';
import { mayContainRefs, RefText } from './RefLink.js';
import { Sheet } from './Sheet.js';
function spanStyle(s) {
    if (!s.fg && !s.bg && !s.inverse)
        return undefined;
    const fg = s.inverse ? (s.bg ?? 'var(--vk-code-bg)') : s.fg;
    const bg = s.inverse ? (s.fg ?? 'var(--vk-text-1)') : s.bg;
    return { color: fg, backgroundColor: bg };
}
function spanClass(s) {
    return (cx(s.bold && 'vk-b', s.dim && 'vk-dim', s.italic && 'vk-i', s.underline && 'vk-u') || undefined);
}
function highlightMatches(text, q, onMatch, active) {
    if (!q)
        return mayContainRefs(text) ? _jsx(RefText, { text: text }) : text;
    const out = [];
    const lower = text.toLowerCase();
    const needle = q.toLowerCase();
    let i = 0;
    for (let k = lower.indexOf(needle); k >= 0; k = lower.indexOf(needle, k + needle.length)) {
        if (k > i)
            out.push(text.slice(i, k));
        const n = onMatch();
        out.push(_jsx("mark", { className: cx('vk-match', n === active && 'is-active'), "data-match": n, children: text.slice(k, k + needle.length) }, k));
        i = k + needle.length;
    }
    if (i < text.length)
        out.push(text.slice(i));
    return out;
}
export function AnsiLine({ spans, query = '', counter, active = -1, }) {
    const c = counter ?? { n: 0 };
    return (_jsx(_Fragment, { children: spans.map((s, i) => (_jsx("span", { className: spanClass(s.style), style: spanStyle(s.style), children: highlightMatches(s.text, query, () => c.n++, active) }, i))) }));
}
/** One terminal line: plain lines get test marks and timings (lib/term.ts); ANSI keeps its own. */
function TermLine({ spans }) {
    const plain = spans.every((s) => !s.style.fg && !s.style.bg && !s.style.inverse);
    if (!plain)
        return _jsx(AnsiLine, { spans: spans });
    const text = spans.map((s) => s.text).join('');
    return (_jsx(_Fragment, { children: termPieces(text).map((p, i) => p.tone ? (_jsx("span", { className: `vk-term--${p.tone}`, children: p.text }, i)) : (_jsx(Fragment, { children: mayContainRefs(p.text) ? _jsx(RefText, { text: p.text }) : p.text }, i))) }));
}
/**
 * Tool output: head + tail with "+N lines" in between. Tapping opens the full output in a
 * sheet with search (match count, next/prev) and ANSI colour.
 */
export function OutputBlock({ text, title = 'Output', head = 6, tail = 4, state = 'ok', exitCode, meta, command, defaultOpen = false, terminal = false, }) {
    const lines = useMemo(() => ansiLines(text.replace(/\n$/, '')), [text]);
    const [sheet, setSheet] = useState(defaultOpen);
    const hidden = lines.length - head - tail;
    const clipped = hidden > 1;
    const shown = clipped
        ? [
            ...lines.slice(0, head).map((l, i) => [i, l]),
            ...lines.slice(lines.length - tail).map((l, i) => [lines.length - tail + i, l]),
        ]
        : lines.map((l, i) => [i, l]);
    if (!text) {
        return (_jsx("div", { className: "vk-output vk-output--empty", children: state === 'streaming' ? 'Waiting for output…' : 'No output' }));
    }
    return (_jsxs("div", { className: cx('vk-output', terminal && 'vk-output--term', state === 'error' && 'vk-output--error'), ...(terminal ? { 'data-ansi': '' } : {}), children: [_jsxs("pre", { className: "vk-output__pre", tabIndex: 0, role: "region", "aria-label": `${title}, ${lines.length} lines`, children: [terminal && command ? (_jsxs("span", { className: "vk-output__line vk-term__prompt", children: [_jsx("span", { "aria-hidden": "true", children: "$ " }), command, '\n'] })) : null, shown.map(([n, spans], i) => (_jsxs(Fragment, { children: [clipped && i === head ? (_jsx("button", { type: "button", className: "vk-output__gap", onClick: () => setSheet(true), children: _jsxs("span", { children: ["+", hidden, " lines"] }) })) : null, _jsxs("span", { className: "vk-output__line", children: [terminal ? _jsx(TermLine, { spans: spans }) : _jsx(AnsiLine, { spans: spans }), '\n'] })] }, n)))] }), _jsxs("div", { className: "vk-output__bar", children: [exitCode != null ? (_jsxs("span", { className: cx('vk-output__exit', exitCode === 0 ? 'vk-tone-ok' : 'vk-tone-fail'), children: ["exit ", exitCode] })) : null, meta, _jsx("span", { className: "vk-grow" }), _jsx(CopyButton, { text: stripAnsi(text), label: "Copy output" }), _jsx("button", { type: "button", className: "vk-iconbtn", "aria-label": "Open full output", title: "Full output", onClick: () => setSheet(true), children: _jsx(Maximize2, { size: 14 }) })] }), _jsx(OutputSheet, { open: sheet, onClose: () => setSheet(false), text: text, title: title, command: command, state: state, exitCode: exitCode })] }));
}
export function OutputSheet({ open, onClose, text, title, command, state, exitCode, inline, initialQuery = '', }) {
    const lines = useMemo(() => ansiLines(text.replace(/\n$/, '')), [text]);
    const [query, setQuery] = useState(initialQuery);
    const [active, setActive] = useState(0);
    const total = useMemo(() => {
        if (!query)
            return 0;
        const hay = stripAnsi(text).toLowerCase();
        const needle = query.toLowerCase();
        let n = 0;
        for (let k = hay.indexOf(needle); k >= 0; k = hay.indexOf(needle, k + needle.length))
            n++;
        return n;
    }, [text, query]);
    const go = (d) => {
        if (!total)
            return;
        const next = (active + d + total) % total;
        setActive(next);
        requestAnimationFrame(() => {
            document
                .querySelector(`.vk-outsheet [data-match="${next}"]`)
                ?.scrollIntoView({ block: 'center' });
        });
    };
    const counter = { n: 0 };
    return (_jsx(Sheet, { open: open, onClose: onClose, inline: inline, size: "lg", title: title, subtitle: _jsxs("span", { className: "vk-row-gap", children: [command ? _jsxs("code", { className: "vk-mono vk-trunc", children: ["$ ", command] }) : null, _jsxs("span", { children: [lines.length.toLocaleString(), " lines"] }), exitCode != null ? _jsxs("span", { children: ["exit ", exitCode] }) : null, state ? _jsx(StatusPill, { state: state, size: "sm" }) : null] }), children: _jsxs("div", { className: "vk-outsheet", children: [_jsxs("div", { className: "vk-search", role: "search", children: [_jsx(Search, { size: 16, "aria-hidden": "true" }), _jsx("input", { type: "search", className: "vk-search__input", placeholder: "Search output", "aria-label": "Search output", value: query, onChange: (e) => {
                                setQuery(e.target.value);
                                setActive(0);
                            }, onKeyDown: (e) => {
                                if (e.key === 'Enter')
                                    go(e.shiftKey ? -1 : 1);
                            } }), _jsx("span", { className: "vk-search__count", "aria-live": "polite", children: query ? (total ? `${active + 1} of ${total}` : 'No matches') : '' }), _jsx("button", { type: "button", className: "vk-iconbtn", "aria-label": "Previous match", onClick: () => go(-1), disabled: !total, children: _jsx(ChevronUp, { size: 16 }) }), _jsx("button", { type: "button", className: "vk-iconbtn", "aria-label": "Next match", onClick: () => go(1), disabled: !total, children: _jsx(ChevronDown, { size: 16 }) }), _jsx(CopyButton, { text: stripAnsi(text), label: "Copy all" })] }), _jsx("pre", { className: "vk-outsheet__pre", tabIndex: 0, children: lines.map((spans, i) => (_jsxs("span", { className: "vk-outsheet__line", children: [_jsx("span", { className: "vk-outsheet__no", "aria-hidden": "true", children: i + 1 }), _jsx("span", { className: "vk-outsheet__text", children: _jsx(AnsiLine, { spans: spans, query: query, counter: counter, active: active }) }), '\n'] }, i))) })] }) }));
}
//# sourceMappingURL=OutputBlock.js.map